/**
 * Route Preloading Utility
 *
 * Provides intelligent preloading of route chunks based on user behavior
 * and navigation patterns to improve perceived performance.
 */

// Store for preload functions
type PreloadFunction = () => Promise<unknown>;
const preloadRegistry: Map<string, PreloadFunction> = new Map();

// Track which routes have been preloaded
const preloadedRoutes: Set<string> = new Set();

/**
 * Register a route's preload function
 */
export const registerPreload = (route: string, preloadFn: PreloadFunction): void => {
  preloadRegistry.set(route, preloadFn);
};

/**
 * Preload a specific route
 */
export const preloadRoute = async (route: string): Promise<void> => {
  // Don't preload if already loaded
  if (preloadedRoutes.has(route)) {
    return;
  }

  const preloadFn = preloadRegistry.get(route);
  if (preloadFn) {
    try {
      await preloadFn();
      preloadedRoutes.add(route);
    } catch (error) {
      // Silently fail - preloading is an optimization
      console.debug(`Failed to preload route: ${route}`, error);
    }
  }
};

/**
 * Preload multiple routes
 */
export const preloadRoutes = async (routes: string[]): Promise<void> => {
  await Promise.all(routes.map(preloadRoute));
};

/**
 * Navigation relationship map for intelligent preloading
 * Maps routes to their likely next destinations
 */
const navigationRelationships: Record<string, string[]> = {
  '/login': ['/dashboard', '/welcome'],
  '/dashboard': ['/analytics', '/interviews', '/review-queue', '/associates'],
  '/associates': ['/associates/:id', '/interviews'],
  '/interviews': ['/interview/:id', '/review/:id', '/review-queue'],
  '/review-queue': ['/review/:id', '/interviews'],
  '/templates': ['/templates/:id', '/templates/new', '/questions'],
  '/questions': ['/templates'],
  '/analytics': ['/analytics/journey', '/reports'],
  '/settings': ['/audit-logs', '/backups', '/preferences'],
};

/**
 * Get likely navigation targets based on current route
 */
export const getLikelyTargets = (currentRoute: string): string[] => {
  // Normalize route by removing specific IDs
  const normalizedRoute = currentRoute.replace(/\/[a-f0-9-]{36}/gi, '/:id');
  return navigationRelationships[normalizedRoute] || [];
};

/**
 * Preload likely targets for a given route
 * Uses requestIdleCallback for non-blocking preloading
 */
export const preloadLikelyTargets = (currentRoute: string): void => {
  const targets = getLikelyTargets(currentRoute);

  if (targets.length === 0) return;

  // Use requestIdleCallback if available, otherwise setTimeout
  const schedulePreload = (callback: () => void) => {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(callback, { timeout: 2000 });
    } else {
      setTimeout(callback, 100);
    }
  };

  schedulePreload(() => {
    targets.forEach((target) => {
      // Normalize target for registry lookup
      const normalizedTarget = target.replace('/:id', '');
      preloadRoute(normalizedTarget);
    });
  });
};

/**
 * Preload on link hover - use with NavLink/Link onMouseEnter
 */
export const handleLinkHover = (targetRoute: string): void => {
  // Debounce to avoid excessive preloading
  const normalizedRoute = targetRoute.replace(/\/[a-f0-9-]{36}/gi, '');
  preloadRoute(normalizedRoute);
};

/**
 * Preload on link focus - for keyboard navigation accessibility
 */
export const handleLinkFocus = (targetRoute: string): void => {
  const normalizedRoute = targetRoute.replace(/\/[a-f0-9-]{36}/gi, '');
  preloadRoute(normalizedRoute);
};

/**
 * Create preload handlers for a NavLink
 */
export const createPreloadHandlers = (targetRoute: string) => ({
  onMouseEnter: () => handleLinkHover(targetRoute),
  onFocus: () => handleLinkFocus(targetRoute),
});

/**
 * Check if a route has been preloaded
 */
export const isRoutePreloaded = (route: string): boolean => {
  const normalizedRoute = route.replace(/\/[a-f0-9-]{36}/gi, '');
  return preloadedRoutes.has(normalizedRoute);
};

/**
 * Clear preloaded routes (useful for testing or memory management)
 */
export const clearPreloadedRoutes = (): void => {
  preloadedRoutes.clear();
};

export default {
  registerPreload,
  preloadRoute,
  preloadRoutes,
  getLikelyTargets,
  preloadLikelyTargets,
  handleLinkHover,
  handleLinkFocus,
  createPreloadHandlers,
  isRoutePreloaded,
  clearPreloadedRoutes,
};
